import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  Download,
  Boxes as BoxesIcon,
  ListChecks,
  Tags,
  Truck,
  Inbox,
  DoorOpen,
  Settings,
  ScanLine,
  Sparkles,
  Users,
} from 'lucide-react';
import { Container, Stack } from '@easymoving/ui';
import { DESTINATIONS } from '@easymoving/types';
import { destinationTone } from '../../lib/taxonomy/tone';
import type { BadgeTone } from '@easymoving/ui';
import { InstallCard } from '../../components/app/InstallCard';

const dot: Record<BadgeTone, string> = {
  neutral: 'bg-muted',
  petrol: 'bg-petrol',
  clay: 'bg-clay',
  olive: 'bg-olive',
  amber: 'bg-amber',
  blue: 'bg-blue',
  violet: 'bg-violet',
  rose: 'bg-rose',
  ink: 'bg-ink',
};

function Rowlink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="flex items-center justify-between gap-3 surface px-4 py-3 hover:bg-surface-tint transition-colors">
      <span className="flex items-center gap-3 text-[15px]">{children}</span>
      <ChevronLeft className="size-4 text-graphite-soft" />
    </Link>
  );
}

export function MorePage() {
  const { t, i18n } = useTranslation();
  return (
    <Container size="narrow" className="pt-6">
      <h1 className="font-display text-2xl tracking-snug mb-4">{t('more.title')}</h1>

      <div className="mb-4">
        <InstallCard />
      </div>

      <h2 className="smallcaps text-graphite mb-2">{t('more.views')}</h2>
      <Stack gap="xs">
        <Rowlink to="/app/needs">
          <Inbox className="size-4 text-graphite" />
          {t('more.needs')}
        </Rowlink>
        <Rowlink to="/app/rooms">
          <DoorOpen className="size-4 text-graphite" />
          {t('more.rooms')}
        </Rowlink>
        {DESTINATIONS.map((d) => (
          <Rowlink key={d} to={`/app/items?destination=${d}`}>
            <span className={`size-2.5 rounded-full ${dot[destinationTone(d)]}`} />
            {t(`enum.destination.${d}`)}
          </Rowlink>
        ))}
        <Rowlink to="/app/boxes">
          <BoxesIcon className="size-4 text-graphite" />
          {t('boxes.title')}
        </Rowlink>
      </Stack>

      <h2 className="smallcaps text-graphite mt-6 mb-2">{t('more.tools')}</h2>
      <Stack gap="xs">
        <Rowlink to="/app/scan">
          <ScanLine className="size-4 text-graphite" />
          {t('scan.title')}
        </Rowlink>
        <Rowlink to="/app/share">
          <Users className="size-4 text-graphite" />
          {t('share.title')}
        </Rowlink>
        <Rowlink to="/app/settle-in">
          <Sparkles className="size-4 text-graphite" />
          {t('settleIn.title')}
        </Rowlink>
        <Rowlink to="/app/move">
          <Truck className="size-4 text-graphite" />
          {t('move.setupCta')}
        </Rowlink>
        <Rowlink to="/app/taxonomy">
          <Tags className="size-4 text-graphite" />
          {t('more.manage')}
        </Rowlink>
        <Rowlink to="/app/report">
          <Truck className="size-4 text-graphite" />
          {t('report.title')}
        </Rowlink>
        <Rowlink to="/app/export">
          <Download className="size-4 text-graphite" />
          {t('more.export')}
        </Rowlink>
        <Rowlink to="/app/settings">
          <Settings className="size-4 text-graphite" />
          {t('more.settings')}
        </Rowlink>
        <Rowlink to="/app/progress">
          <ListChecks className="size-4 text-graphite" />
          התקדמות הבנייה
        </Rowlink>
      </Stack>

      <h2 className="smallcaps text-graphite mt-6 mb-2">Legal &amp; privacy</h2>
      <Stack gap="xs">
        {(() => {
          // Link to the marketing/landing pages (prod domain; SPA rewrites serve them)
          const base = 'https://easymoving.io';
          const lng = i18n.resolvedLanguage?.split('-')[0] || 'he';
          const p = (slug: string) => (lng === 'he' ? `/${slug}` : `/${lng}/${slug}`);
          const items = [
            { href: base + p('privacy'), label: 'Privacy policy' },
            { href: base + p('terms'), label: 'Terms of use' },
            { href: base + p('contact'), label: 'Contact' },
            { href: base + p('about'), label: 'About' },
          ];
          return items.map((it) => (
            <a key={it.href} href={it.href} className="flex items-center justify-between gap-3 surface px-4 py-3 hover:bg-surface-tint transition-colors text-[15px]">
              <span>{it.label}</span>
              <ChevronLeft className="size-4 text-graphite-soft" />
            </a>
          ));
        })()}
      </Stack>
    </Container>
  );
}
